/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ActionContext, CLIENT_HYDRATE_KEY, CLIENT_HYDRATE_TYPE } from '@msdyn365-commerce/action-internal';
import { RequestCache, ICacheKey, ICache } from '@msdyn365-commerce/cache-internal';
import {
    CoreContext,
    CultureInfoFormatter,
    IActionContext,
    IAny,
    ICoreContext,
    IDictionary,
    IGeneric,
    IModule,
    IModuleContract,
    IMSDyn365Window,
    IPartnerThemeSettings,
    IRequestContext,
    IRequestContextUrl,
    IVariants,
    msdyn365Commerce
} from '@msdyn365-commerce/core-internal';
import {
    ClientAppInsightsJSLogger,
    LoggerType,
    EVENT_CLIENT_RENDER_START,
    EVENT_CLIENT_RENDER_STOP,
    CONTENT_SECURITY_POLICY_ERROR,
    EVENT_WEB_VITALS_CLS,
    EVENT_WEB_VITALS_FCP,
    EVENT_WEB_VITALS_FID,
    EVENT_WEB_VITALS_LCP,
    EVENT_WEB_VITALS_TTFB,
    EXCEPTION_CLIENT_CACHE_HYDRATE,
    EXCEPTION_LOAD_DATA_EXCEPTION,
    EXCEPTION_PRODUCT_LIST_CLIENTSIDE_HYDRATION,
    expAttributeRenderer,
    GenericConsoleLogger,
    IEventClientRenderMetrics,
    IEventClientRenderProperties,
    IEventClientWebVitalsCLSMetrics,
    IEventId,
    IEventTime,
    ITelemetry,
    LogLevel,
    MESSAGE_CLIENT_RENDER_ERROR,
    StaticTelemetry,
    Telemetry,
    TelemetryEvent,
    InternalTelemetry,
    TSuccessNumber
} from '@msdyn365-commerce/telemetry-internal';
import axios from 'axios';
import 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
// eslint-disable-next-line import/no-unassigned-import
import 'lazysizes/plugins/bgset/ls.bgset';
import { partition as _partition } from 'lodash';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { getCLS, getFCP, getFID, getLCP, getTTFB } from 'web-vitals';
import {
    RSRequestInterceptor,
    RSRequestInterceptorErrorHandler,
    RSResponseInterceptor,
    RSResponseInterceptorErrorHandler
} from '../_server/inteceptors/rs-logging-interceptor';
import SafeRenderModule from '../components/safe-render-module';
import {
    CLIENT_RENDER_COMPLETE_EVENT,
    CLIENT_RENDER_START_EVENT,
    DEPRECATED_CLIENT_RENDER_COMPLETE_EVENT,
    LAYOUT_MOUNT_POINT,
    PROCESS_TYPE,
    MODULE_CONFIG_KEY,
    CLIENT_ENTRY_POINTS_TYPE
} from '../consts';
import { loadData } from '../data/load-data';
import { processComplexDataTypes } from '../hydrators/process-page-response';
import { IDisabledProperties } from '../store/authoring-edit-helper';
import { PageContext } from '../store/page-context';
import {
    getCurrencyCodeFromContext,
    isLazyRenderModuleEnabled,
    optimizedGetAllModules,
    shouldOptimizeHydrate,
    timeSliceLongJSTask
} from '../utils/helpers';
import { SessionStorageContext } from '../utils/session-storage-utils';
import { CookieContext } from './../utils/cookie-utils';
import { resolveMissingSegmentsOnClient } from './../utils/resolve-segments-utils';

type windowinitialData = IGeneric<IAny>;

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface ClientWindow extends IMSDyn365Window {
        ___initialData___: windowinitialData;
        cache: RequestCache;
    }
}

declare let window: ClientWindow;

axios.interceptors.request.use(RSRequestInterceptor, RSRequestInterceptorErrorHandler);
axios.interceptors.response.use(RSResponseInterceptor, RSResponseInterceptorErrorHandler);

const CLIENT_HYDRATE_CACHE_KEY: ICacheKey = {
    key: CLIENT_HYDRATE_KEY,
    typeName: CLIENT_HYDRATE_TYPE
};

export const getRequestContext = (storeData: windowinitialData): IRequestContext => {
    const requestContext: IRequestContext = storeData.requestContext;

    // See if cookie compliance is disabled at the site level
    const disableCookieCompliance =
        (requestContext.app && requestContext.app.platform && requestContext.app.platform.disableCookieCompliance) || false;
    const cartSessionExpiration =
        (requestContext.app && requestContext.app.platform && requestContext.app.platform.cartSessionExpiration) || 0;
    const cookies = new CookieContext({
        isConsentRequired: !disableCookieCompliance,
        cartExpirationInDays: cartSessionExpiration,
        sameSiteRequired: requestContext.sameSiteRequired
    });

    try {
        // merge the query params from url back to request context in case if the page response is cached
        const urlSearchParams = new URLSearchParams(window.location.search);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const entries = Object.fromEntries((urlSearchParams as any).entries());
        Object.keys(entries || {}).map(param => {
            if (requestContext.query && !requestContext.query[param]) {
                requestContext.query[param] = entries[param] || '';
            }
        });
    } catch (e) {
        console.error('merging query parameters from url failed');
    }

    requestContext.cookies = cookies;
    requestContext.sessionStorage = new SessionStorageContext(cookies);
    requestContext.headers = Object.freeze({});
    requestContext.url.requestUrl =
        typeof requestContext.url.requestUrl === 'string' ? new URL(requestContext.url.requestUrl) : requestContext.url.requestUrl;
    return requestContext;
};

const setClientHydrateContext = (requestCache: ICache) => {
    requestCache.put(CLIENT_HYDRATE_CACHE_KEY, { item: true });
};

const resetClientHydrateContext = (requestCache: ICache) => {
    requestCache.del(CLIENT_HYDRATE_CACHE_KEY);
};

const getPathName = (requestContextUrl: IRequestContextUrl): string => {
    if (requestContextUrl && requestContextUrl.requestUrl && requestContextUrl.requestUrl.pathname) {
        return requestContextUrl.requestUrl.pathname;
    }
    return '';
};

const isPaymentVerification = (requestContext: IRequestContext): boolean => {
    return requestContext.query?.pv === '1';
};

const isCheckoutRoute = (requestContext: IRequestContext): boolean => {
    return (
        requestContext.app &&
        requestContext.app.routes &&
        requestContext.app.routes.checkout &&
        requestContext.app.routes.checkout.destinationUrl &&
        getPathName(requestContext.url) === requestContext.app.routes.checkout.destinationUrl
    );
};

const activateExperiments = (requestContext: IRequestContext): void => {
    // Activate experiment if an experimentation connector has been registered and active experiments are provided
    // In a preview scenario, OneRF will not send a user ID and which we will not active the experiment
    if (
        msdyn365Commerce.experimentationConnector &&
        requestContext.features.experimentation &&
        requestContext.experiments &&
        requestContext.experiments.userId &&
        requestContext.experiments.activeExperiments &&
        requestContext.experiments.activeExperiments.length > 0
    ) {
        const experimentInfo = requestContext.experiments;
        // Split the experiment list into two lists, one whose experiments are coupled with a module and one whose experiments are not
        const [experimentsWithoutModuleId, experimentsWithModuleId] = _partition(experimentInfo.activeExperiments, (value: IVariants) => {
            return !value.moduleId;
        });
        // Activate experiments not tied to a module id
        msdyn365Commerce.experimentationConnector.provider.activateExperiment(experimentInfo.userId, experimentsWithoutModuleId, {
            Type: requestContext.device.Type
        });

        // Activate experiments once 30% of the module is visible
        const options: IntersectionObserverInit = {
            threshold: 0.3
        };
        const experimentMap: IDictionary<IVariants> = {};
        const experimentObserver = new IntersectionObserver((entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
                    const activatedExpId = entry.target.getAttribute(`data-exp`);
                    if (activatedExpId) {
                        // Find the experiment/variant combo associated with the experiment to activate
                        msdyn365Commerce.experimentationConnector?.provider.activateExperiment(
                            experimentInfo.userId,
                            [experimentMap[activatedExpId]],
                            {
                                Type: requestContext.device.Type
                            }
                        );
                    }
                    // Once we the element has come into the view for the first time, we can stop observing it
                    observer.unobserve(entry.target);
                }
            });
        }, options);
        for (let i = 0; i < experimentsWithModuleId.length; i++) {
            const currentExperiment = experimentsWithModuleId[i];
            const target = document.querySelector(`[data-exp="id:${currentExperiment.experimentId}"]`);
            if (target) {
                experimentMap[`id:${currentExperiment.experimentId}`] = currentExperiment;
                experimentObserver.observe(target);
            }
        }
    }
};

/**
 * Method to hydrate the markup returned by server on the client.
 * @param requestContext request context object
 * @param pageContext page context object
 * @param coreContext core context object
 */
const hydrateClient = (requestContext: IRequestContext, pageContext: PageContext, coreContext: ICoreContext) => {
    const pageRoot = pageContext.pageRoot;
    const body = pageRoot.modules && pageRoot.modules.body;
    const props = body && body.length > 0 ? body[0] : pageRoot;
    const defaultPageConfig =
        (!!!requestContext?.features?.disable_moduleConfig_optimize &&
            pageContext.requestCache.getValue({ typeName: MODULE_CONFIG_KEY, key: props.id })) ||
        props.config;
    try {
        ReactDOM.hydrate(
            <CoreContext.Provider value={coreContext}>
                <SafeRenderModule {...props} config={defaultPageConfig} store={pageContext} root={true} />
            </CoreContext.Provider>,
            document.getElementById(LAYOUT_MOUNT_POINT)
        );
    } catch (err) {
        // If in dev mode or debug mode, throw the error so it's visible on the page.
        if ((process.env.NODE_ENV === 'development' || requestContext.params.isDebug) && !requestContext.params.isEditor) {
            throw err;
            // In editor mode, catch the error but don't throw so that the page doesn't freeze up
        } else if (requestContext.params.isEditor) {
            console.log(`Error running editor (client) render: ${err}`);
        }
    }
};

/**
 * Method to initialize authoring helper and register event to receive list of disabled properties when rendered in the context of site builder
 * @param requestContext request context object
 * @param pageContext page context object
 * @param coreContext core context object
 * @param actionContext action context object
 * @param moduleTelemetry module telemetry object
 */
const initAuthoringHelper = (
    requestContext: IRequestContext,
    pageContext: PageContext,
    coreContext: ICoreContext,
    actionContext: IActionContext,
    moduleTelemetry
) => {
    const themeSettings: IPartnerThemeSettings = window.___initialData___.themeSettings ? window.___initialData___.themeSettings : {};

    return import('../utils/authoring-tools-helper').then(m => {
        const AuthoringToolsHelper = m.AuthoringToolsHelper;
        const authoringHelper = new AuthoringToolsHelper(actionContext, themeSettings, coreContext, pageContext, moduleTelemetry);
        window._msdyn365.authoringHelper = authoringHelper;

        const _msdyn365Data = window._msdyn365;
        _msdyn365Data.authoringHelper = authoringHelper;

        if (requestContext.params.isEditor) {
            // @ts-ignore
            document.addEventListener('_msdyn365_client_disabled_properties', (dpEvent: { detail: IDisabledProperties }) => {
                if (dpEvent && dpEvent.detail && dpEvent.detail) {
                    Object.keys(dpEvent.detail.modules).map(key => {
                        authoringHelper.updateDisabledProperties(key, dpEvent.detail.modules[key]);
                    });
                }
            });
        }
    });
};

export function clientRender(): void {
    const storeData = window.___initialData___;
    if (!storeData || typeof storeData !== 'object') {
        console.error(MESSAGE_CLIENT_RENDER_ERROR);
        return;
    }
    const removePlaceholderModules = (modulesDictionary: { [slot: string]: IModuleContract[] }) => {
        Object.keys(modulesDictionary || {}).forEach(slot => {
            modulesDictionary[slot] = modulesDictionary[slot].filter(a => a.typeName !== 'place-holder-for-container-preview');
            modulesDictionary[slot].forEach(module => module.modules && removePlaceholderModules(module.modules));
        });
    };
    const requestContext = getRequestContext(storeData);
    if (requestContext.params.isEditor) {
        removePlaceholderModules(storeData.pageRoot.modules);
    }

    // if request is not POST or is not a checkout route or is not a payment verification, clear all checkout session storage data.
    // TODO: Explore a way to move this logic to SSK
    if (!requestContext.requestFormData || !isCheckoutRoute(requestContext) || !isPaymentVerification(requestContext)) {
        requestContext.sessionStorage.removeCheckoutCartData();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const appCache = (msdyn365Commerce as any).getAppCache(requestContext);
    const pageContext = new PageContext(storeData, requestContext, appCache);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__msdyn365__ = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__msdyn365__.pageContext = pageContext;

    // Get the 1DS instance from the browser client. Initialized as part of base-page
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const oneDSWeb: any = window['oneDSWeb'];

    // Get the registered experiment listener
    const expListener = msdyn365Commerce.experimentationConnector?.listener;

    // Get application insights instrumentation key
    const appInsightsInstrumentationKey = requestContext.telemetrySettings.instrumentationKey || '';
    let msftClientAppInsightsJSLogger;
    if (appInsightsInstrumentationKey !== '') {
        msftClientAppInsightsJSLogger = new ClientAppInsightsJSLogger(
            LoggerType.MSFTApplicationInsightsLogger,
            appInsightsInstrumentationKey
        );
    }

    // Get operational insights instrumentation key
    const operationalInsightsInstrumentationKey = requestContext.telemetrySettings.operationalInsightsInstrumentationKey || '';
    let operationalClientAppInsightsJSLogger;
    if (operationalInsightsInstrumentationKey !== '') {
        operationalClientAppInsightsJSLogger = new ClientAppInsightsJSLogger(
            LoggerType.OperationalApplicationInsightsLogger,
            operationalInsightsInstrumentationKey
        );
    }

    // Set up internal SDK telemetry
    const internalTelemetry = StaticTelemetry;
    internalTelemetry.enableDataAnalytics(requestContext.features.enableDataAnalytics);
    internalTelemetry.registerWebAnalyticsPlugin(oneDSWeb);
    internalTelemetry.registerAttributeRenderer(expAttributeRenderer);
    if (appInsightsInstrumentationKey !== '') {
        internalTelemetry.registerTraceLogger({
            traceLogger: msftClientAppInsightsJSLogger
        });
    }
    if (operationalInsightsInstrumentationKey !== '') {
        internalTelemetry.registerTraceLogger({
            traceLogger: operationalClientAppInsightsJSLogger
        });
    }

    internalTelemetry.setTelemetryRequestContext({
        siteId: '[[Placeholder]]',
        channelId: requestContext.channel?.RecordId ? requestContext.channel.RecordId.toString() : 'Unavailable',
        pageType: requestContext.urlTokens.pageType,
        pageId: pageContext.pageRoot.id
    });

    const correlationId = Telemetry.generateGuid();
    logWebVitals(internalTelemetry, correlationId);

    const targetId = requestContext?.tuid;
    if (targetId) {
        resolveMissingSegmentsOnClient(requestContext, targetId, internalTelemetry);
    }

    // Set up external module telemetry
    const moduleTelemetry = new Telemetry();
    moduleTelemetry.enableDataAnalytics(requestContext.features.enableDataAnalytics);
    moduleTelemetry.registerWebAnalyticsPlugin(oneDSWeb);
    moduleTelemetry.registerAttributeRenderer(expAttributeRenderer);
    if (appInsightsInstrumentationKey !== '') {
        moduleTelemetry.registerTraceLogger({
            traceLogger: msftClientAppInsightsJSLogger
        });
    }

    // If experimentaiton listener set up, add to event logging framework
    if (expListener && requestContext.features.experimentation) {
        internalTelemetry.registerExpLogger(expListener);

        moduleTelemetry.registerExpLogger(expListener);
    }

    // If in debug mode, add console loggers
    if (requestContext.params.isDebug) {
        internalTelemetry.registerTraceLogger({ traceLogger: GenericConsoleLogger, options: { isDebug: requestContext.params.isDebug } });
        moduleTelemetry.registerTraceLogger({ traceLogger: GenericConsoleLogger, options: { isDebug: requestContext.params.isDebug } });
    }

    const contentSecurityPolicy = requestContext.app && requestContext.app?.contentSecurityPolicy;

    // Logging CSP error only when CSP is enabled and enableReportOnlyMode is false
    if (contentSecurityPolicy && !contentSecurityPolicy?.disableContentSecurityPolicy && !contentSecurityPolicy?.enableReportOnlyMode) {
        const randomNumberGenerator = Math.floor(Math.random() * 100);

        document.addEventListener('securitypolicyviolation', e => {
            const cspReport = JSON.stringify({
                'blocked-uri': e.blockedURI,
                'document-uri': e.documentURI,
                'original-policy': e.originalPolicy,
                'violated-directive': e.violatedDirective
            });

            // Only logging all CSP errors for 25% of page request
            if (randomNumberGenerator < 25) {
                internalTelemetry.log(LogLevel.Error, CONTENT_SECURITY_POLICY_ERROR, { customTags: [cspReport] });
            }
        });
    }

    pageContext
        .putRequestedModulesInCacheOnClient(storeData, moduleTelemetry)
        .catch(error => {
            internalTelemetry.log(LogLevel.Error, EXCEPTION_CLIENT_CACHE_HYDRATE, { exception: error });
        })
        .finally(async () => {
            await timeSliceLongJSTask(requestContext);

            window._msdyn365 = {
                requestContext: requestContext,
                telemetry: internalTelemetry
            };

            const actionContext = new ActionContext({
                id: 'Client Action Context',
                requestContext: requestContext,
                requestCache: pageContext.requestCache,
                telemetry: moduleTelemetry,
                appCache: null,
                spawnChildOnExecute: true
            });

            if (requestContext.params.isDebug) {
                window._msdyn365.cache = pageContext.requestCache;
            }
            const coreContext: ICoreContext = {
                actionContext: actionContext,
                app: requestContext.app,
                // @ts-ignore -- we don't actually have this prop defined on the interface
                internalTelemetry: internalTelemetry,
                telemetry: moduleTelemetry,
                request: requestContext,
                cultureFormatter: new CultureInfoFormatter(
                    requestContext.locale,
                    getCurrencyCodeFromContext(requestContext),
                    requestContext.query
                )
            };
            let allModules: IModule[] = [];
            await timeSliceLongJSTask(requestContext);
            if (requestContext?.features?.loadData_optimization) {
                allModules = await optimizedGetAllModules(pageContext, requestContext);
            } else {
                allModules = await pageContext.allModules(requestContext, []);
            }

            const allowDispatchEvent = requestContext?.params?.isEditor || requestContext?.features?.enable_verbose_logs ? true : false;
            if (shouldOptimizeHydrate(requestContext)) {
                await optimizeClientHydrate(
                    requestContext,
                    pageContext,
                    coreContext,
                    actionContext,
                    allModules,
                    moduleTelemetry,
                    internalTelemetry,
                    correlationId
                );
            } else {
                if (requestContext.params.isEditor) {
                    await initAuthoringHelper(requestContext, pageContext, coreContext, actionContext, moduleTelemetry);
                }

                // Fire event to let tooling know render starts. Tools on receiving this event, calls
                // initialziation and passes the rendering helper class instance that can be used by SDK to
                // get any information/context w.r.t tools
                if (allowDispatchEvent) {
                    const event = new Event(CLIENT_RENDER_START_EVENT);
                    document.dispatchEvent(event);
                }

                if (allowDispatchEvent) {
                    internalTelemetry.trackEvent<IEventClientRenderProperties, {}>(EVENT_CLIENT_RENDER_START, {
                        id: correlationId
                    });
                    internalTelemetry.startTimer(correlationId);
                }

                let initialPromise: Promise<void> = Promise.resolve();
                const LAZYLOAD_ALL_SWITCH: boolean = requestContext.features.lazyload_all;

                initialPromise = initialPromise.then(async () => {
                    try {
                        return processComplexDataTypes(LAZYLOAD_ALL_SWITCH, PROCESS_TYPE.client, allModules, actionContext);
                    } catch (error) {
                        internalTelemetry.log(LogLevel.Error, EXCEPTION_PRODUCT_LIST_CLIENTSIDE_HYDRATION, { exception: error });
                    }
                });

                initialPromise.finally(() => {
                    // put client hydrate flag into request cache.
                    setupClientHydrateFlag(pageContext.requestCache);
                    loadData(allModules, requestContext, internalTelemetry, moduleTelemetry, pageContext.requestCache, null)
                        .then(() => {
                            if (allowDispatchEvent) {
                                internalTelemetry.trackEvent<IEventClientRenderProperties, IEventClientRenderMetrics>(
                                    EVENT_CLIENT_RENDER_STOP,
                                    {
                                        id: correlationId
                                    },
                                    {
                                        success: 1,
                                        time: internalTelemetry.stopTimer(correlationId)
                                    }
                                );
                            }
                        })
                        .catch(error => {
                            if (allowDispatchEvent) {
                                internalTelemetry.trackEvent<IEventClientRenderProperties, IEventClientRenderMetrics>(
                                    EVENT_CLIENT_RENDER_STOP,
                                    {
                                        id: correlationId
                                    },
                                    {
                                        success: 0,
                                        time: internalTelemetry.stopTimer(correlationId)
                                    }
                                );
                            }
                            internalTelemetry.log(LogLevel.Error, EXCEPTION_LOAD_DATA_EXCEPTION, { exception: error });
                        })
                        .finally(() => {
                            // remove client hydrate flag from request cache.
                            resetClientHydrateContext(pageContext.requestCache);
                            // Gets a reference to the same Telemetry, so that we can route existing log calls to new back-end without changing front-end API

                            if (!shouldOptimizeHydrate(requestContext)) {
                                hydrateClient(requestContext, pageContext, coreContext);
                            }
                            msdyn365Commerce.setClientHydrate(false);
                            // Add the page view exp event id to the page level div
                            const pageName = requestContext.telemetryPageName || requestContext.url.requestUrl.pathname;
                            document.getElementById(LAYOUT_MOUNT_POINT)?.setAttribute('data-exp-event-id', `${pageName}.PageView`);
                            // Activate any active experiments on the page
                            activateExperiments(requestContext);

                            // TODO (matt, 6/15/20): Manually firing page view event, undo once 1DS forwarding to framework implemented
                            internalTelemetry.logEvent(TelemetryEvent.PageView, {
                                title: requestContext.telemetryPageName || requestContext.url.requestUrl.pathname
                            });

                            if (allowDispatchEvent) {
                                // Fire event to let tooling know render is complete
                                const deprecatedEvent = new Event(DEPRECATED_CLIENT_RENDER_COMPLETE_EVENT);
                                document.dispatchEvent(deprecatedEvent);

                                document.dispatchEvent(new Event(CLIENT_RENDER_COMPLETE_EVENT));
                            }
                        });
                });
            }
        });

    // TODO (matt, 6/15/20): Manually firing page view event, undo once 1DS forwarding to framework implemented
    internalTelemetry.logEvent(TelemetryEvent.PageView, {
        title: requestContext.telemetryPageName || requestContext.url.requestUrl.pathname
    });
}

const setupClientHydrateFlag = (requestCache: ICache) => {
    setClientHydrateContext(requestCache);
    msdyn365Commerce.setClientHydrate(true);
};

const optimizeClientHydrate = async (
    requestContext: IRequestContext,
    pageContext: PageContext,
    coreContext: ICoreContext,
    actionContext: ActionContext,
    allModules: IModule[],
    moduleTelemetry: ITelemetry,
    internalTelemetry: InternalTelemetry,
    correlationId: string
) => {
    try {
        // Await on instance data actions, other data actions are run asynchronously
        setupClientHydrateFlag(pageContext.requestCache);

        let modulesRenderedOnServer = allModules;
        if (isLazyRenderModuleEnabled(requestContext)) {
            modulesRenderedOnServer = allModules.filter(m => {
                const cacheItem = pageContext.requestCache.get<string[]>({ typeName: CLIENT_ENTRY_POINTS_TYPE, key: m.typeName })?.item;
                return !cacheItem;
            });
        }
        await timeSliceLongJSTask(requestContext);

        await loadData(modulesRenderedOnServer, requestContext, internalTelemetry, moduleTelemetry, pageContext.requestCache, null);
        resetClientHydrateContext(pageContext.requestCache);
        const allowDispatchEvent = requestContext?.params?.isEditor || requestContext?.features?.enable_verbose_logs ? true : false;

        // Fire event to let tooling know render starts. Tools on receiving this event, calls
        // initialziation and passes the rendering helper class instance that can be used by SDK to
        // get any information/context w.r.t tools
        if (allowDispatchEvent) {
            const event = new Event(CLIENT_RENDER_START_EVENT);
            document.dispatchEvent(event);
        }

        if (requestContext?.features?.enable_verbose_logs) {
            internalTelemetry.trackEvent<IEventClientRenderProperties, {}>(EVENT_CLIENT_RENDER_START, {
                id: correlationId
            });
            internalTelemetry.startTimer(correlationId);
        }

        // hydrate client without waiting on all the data actions to complete
        hydrateClient(requestContext, pageContext, coreContext);
        msdyn365Commerce.setClientHydrate(false);

        if (allowDispatchEvent) {
            trackEvent(internalTelemetry, correlationId, EVENT_CLIENT_RENDER_STOP, 1);
        }

        if (requestContext.params.isEditor) {
            await initAuthoringHelper(requestContext, pageContext, coreContext, actionContext, moduleTelemetry);
        }

        const LAZYLOAD_ALL_SWITCH: boolean = requestContext.features.lazyload_all;

        try {
            await processComplexDataTypes(LAZYLOAD_ALL_SWITCH, PROCESS_TYPE.client, allModules, actionContext);
        } catch (error) {
            internalTelemetry.log(LogLevel.Error, EXCEPTION_PRODUCT_LIST_CLIENTSIDE_HYDRATION, { exception: error });
        }

        // Add the page view exp event id to the page level div
        const pageName = requestContext.telemetryPageName || requestContext.url.requestUrl.pathname;
        document.getElementById(LAYOUT_MOUNT_POINT)?.setAttribute('data-exp-event-id', `${pageName}.PageView`);
        // Activate any active experiments on the page
        activateExperiments(requestContext);

        if (allowDispatchEvent) {
            // Fire event to let tooling know render is complete
            const deprecatedEvent = new Event(DEPRECATED_CLIENT_RENDER_COMPLETE_EVENT);
            document.dispatchEvent(deprecatedEvent);
            document.dispatchEvent(new Event(CLIENT_RENDER_COMPLETE_EVENT));
        }
    } catch (error) {
        if (requestContext.features.enable_verbose_logs) {
            trackEvent(internalTelemetry, correlationId, EVENT_CLIENT_RENDER_STOP, 0);
        }
        internalTelemetry.log(LogLevel.Error, EXCEPTION_LOAD_DATA_EXCEPTION, { exception: error });
    }
};

const trackEvent = (internalTelemetry: InternalTelemetry, correlationId: string, eventName: string, success: TSuccessNumber) => {
    internalTelemetry.trackEvent<IEventClientRenderProperties, IEventClientRenderMetrics>(
        eventName,
        {
            id: correlationId
        },
        {
            success,
            time: internalTelemetry.stopTimer(correlationId)
        }
    );
};

const logWebVitals = (internalTelemetry: InternalTelemetry, correlationId: string) => {
    getCLS(metric => {
        internalTelemetry.trackEvent<IEventId, IEventClientWebVitalsCLSMetrics>(
            EVENT_WEB_VITALS_CLS,
            { id: correlationId },
            { value: metric.value }
        );
    });
    getFCP(metric => {
        internalTelemetry.trackEvent<IEventId, IEventTime>(EVENT_WEB_VITALS_FCP, { id: correlationId }, { time: metric.value });
    });
    getFID(metric => {
        internalTelemetry.trackEvent<IEventId, IEventTime>(EVENT_WEB_VITALS_FID, { id: correlationId }, { time: metric.value });
    });
    getLCP(metric => {
        internalTelemetry.trackEvent<IEventId, IEventTime>(EVENT_WEB_VITALS_LCP, { id: correlationId }, { time: metric.value });
    });
    getTTFB(metric => {
        internalTelemetry.trackEvent<IEventId, IEventTime>(EVENT_WEB_VITALS_TTFB, { id: correlationId }, { time: metric.value });
    });
};
